import React, { useCallback, useEffect, useRef, useState } from "react";
import theme from "../../data/theme";

/**
 * BoardScale — how big the roster text is, on THIS device.
 *
 * ══════════════════════════════════════════════════════════════════════
 * 🔴 NO RESPONSIVE CSS CAN SOLVE THIS, SO DO NOT REACH FOR ONE.
 *
 * Casting mirrors the tab AT THE SAME CSS PIXEL WIDTH. A 65" TV across the
 * room reports the same ~1920px a laptop does — it is not bigger, it is
 * FURTHER AWAY. So `vw`, `clamp()`, `@media` and every other responsive tool
 * is blind to the one case this exists for: they will test perfectly on a
 * monitor and change nothing in the room. It has to be an explicit control.
 *
 * 🔴 PER DEVICE, AND NEVER IN BOARD STATE. A phone, a laptop and a cast TV all
 * want different values, and /watch is already one page per viewer, so each
 * viewer sets their own. Putting it on the board would push one person's TV
 * setting onto eleven phones — and it would be published, which a display
 * preference has no business being.
 *
 * ⚠️ Browser zoom (Ctrl +) already does all of this with no code at all. The
 * in-product control exists because eleven people will not discover that, and
 * a cast device often has no keyboard attached at all.
 * ══════════════════════════════════════════════════════════════════════
 */
export const BOARD_SCALE_KEY = "mock1_board_scale_v1";

// "Normal" is exactly 1 — the board as it shipped — so the control can never
// be the reason the laptop view changed.
export const SCALE_PRESETS = [
  { key: "normal", label: "Normal", scale: 1 },
  { key: "large", label: "Large", scale: 1.35 },
  { key: "tv", label: "TV", scale: 1.8 },
];

const MIN_SCALE = SCALE_PRESETS[0].scale;
const MAX_SCALE = SCALE_PRESETS[SCALE_PRESETS.length - 1].scale;

/**
 * The largest preset, read off the list rather than typed again — a second copy
 * of 1.8 is how the preset and the layout that keys off it drift apart.
 */
export const TV_SCALE = MAX_SCALE;

/**
 * Whether this device is in TV mode: the page drops its max-width and gutters
 * and the roster rows carry whole names.
 *
 * 🔴 OPT-IN, NEVER A FALLBACK. Anything unreadable answers false, so a junk
 * localStorage value cannot silently reflow a laptop to full bleed. The wide
 * layout only happens when somebody actually chose the largest preset.
 */
export function isTvMode(scale) {
  const n = Number(scale);
  return Number.isFinite(n) && n >= TV_SCALE;
}

/**
 * ROW PITCH AT TV — fixed pixels, deliberately NOT derived from the type.
 *
 * ══════════════════════════════════════════════════════════════════════
 * 🔴 THIS IS THE VERTICAL LEVER, AND IT IS NOT THE FONT SIZE.
 *
 * Slot rows used to take their padding, their gap and their line box from the
 * same factor as the glyphs, so every step of type cost height TWICE — once for
 * the letters and again for the air around them. Sixteen slots multiply that by
 * sixteen, and two card rows multiply it again. Decoupling the pitch buys ~28%
 * of card height at an unchanged font: 1278px -> 918px for two rows at 18px
 * names.
 *
 * 🔴 EVERY ROW GETS THE SAME TREATMENT, STARTERS AND BENCH ALIKE. Ben ruled out
 * a cheaper bench and he is right — all sixteen names are equally important,
 * and a board whose last six rows are second-class is lying about what a roster
 * is. There is no per-slot-type pitch and there must not be one.
 *
 * The values are floors, not preferences: 1px of padding and a 1.05 line box is
 * about as tight as text can be set before descenders start touching the row
 * below. The tag chip is tightened to match, because once the line box is tight
 * the CHIP becomes the tallest thing in the row and silently sets the pitch.
 * ══════════════════════════════════════════════════════════════════════
 */
export const TV_ROW_PITCH = {
  padY: 1,            // vertical padding per slot row
  gap: 2,             // between slot rows
  lineHeight: 1.05,   // the player name's line box
  tagPadY: 1,         // the position chip, or it becomes the binding element
  tagLineHeight: 1.1,
};

/**
 * The type factor at TV, on top of the preset.
 *
 * ══════════════════════════════════════════════════════════════════════
 * 🔴 IT IS BELOW 1, AND THAT IS THE HONEST ANSWER RATHER THAN THE WANTED ONE.
 *
 * CASTING DROPS THE SOURCE DISPLAY TO 1080p. The budget is therefore ~1010px of
 * viewport less ~152px of board chrome — about 858px for the grid — NOT the
 * 1290px a 1440p screen suggested. Ten teams at sixteen slots is two card rows,
 * and a cast TV is the one screen nobody can scroll, so overflow is not
 * cosmetic: it is roster rows that cannot be reached.
 *
 * With the pitch decoupled above, two rows of sixteen-slot cards measure:
 *
 *   factor   name font   two card rows   fits 858px?
 *   0.80     14px        788px           yes
 *   0.90     16px        835px           yes   <- shipped
 *   0.95     17px        880px           no  (+22)
 *   1.00     18px        918px           no  (+60)
 *   1.15     21px        1077px          no  (+219)
 *
 * So 21px — shipped briefly and asked for again — overflows by 219px, and even
 * today's unboosted 18px overflows by 60px. THE TV PRESET HAS NEVER FIT A 1080p
 * CAST for a sixteen-slot ten-team league; the tight pitch is what makes it fit
 * at all, and 16px is the largest type that does.
 *
 * 16px is still the biggest preset — Large is 14px — but it is smaller than the
 * 18px TV used to render, and pretending otherwise would mean shipping a board
 * whose bottom rows are off-screen on draft night.
 *
 * ⚠️ THE ONLY WAYS PAST THIS are fewer visible slots (ruled out), a taller cast
 * (1440p would take 1.15), or accepting a scroll (unacceptable on a TV). Trim
 * another ~40px of chrome and 0.95/17px becomes reachable. A test models the
 * card from these same constants and fails if this is raised.
 *
 * ══════════════════════════════════════════════════════════════════════
 * 🔴 THE 858 ABOVE IS UNDER REVIEW AND IS BELIEVED WRONG — Aug 12 2026.
 *
 * `VIEWPORT − BOARD_CHROME` has NO BOTTOM TERM. The entry bar is pinned to the
 * viewport bottom — 150px of reserved page padding, ~115px of actual bar — and
 * it is not subtracted anywhere in that derivation. The budget for the grid is
 * `innerHeight − chrome above − the bar`, which at 1080p is nearer 585-732 than
 * 858, and BOTH of those are below the ~748 a 15-slot 10-team board needs at the
 * 0.80 floor.
 *
 * If that holds, a 1080p cast has never fit at any rung, and the conclusion
 * above that "16px is the largest type that does" was wrong when it was written
 * — not made wrong by the nomination-height reserve, which came later and made
 * it worse rather than causing it.
 *
 * ⚠️ NOTHING HERE IS CHANGED ON THAT SUSPICION. TV_TYPE_FACTOR stays 0.9 and
 * every number above is left as it was written, because the fix is not a
 * different guess — it is Ben's four snippet runs, which are what settle it.
 * Full working, and what to do if it is confirmed (say so on screen rather than
 * render rows nobody can reach):
 * docs/planning/builder-prompts-chooser-arithmetic-aug12.md
 * ══════════════════════════════════════════════════════════════════════
 */
export const TV_TYPE_FACTOR = 0.9;

/** The multiplier every size in the roster board is derived from. */
export function typeScale(scale) {
  return isTvMode(scale) ? Number(scale) * TV_TYPE_FACTOR : scale;
}

/**
 * The stored scale, or 1.
 *
 * Clamped rather than trusted: this value multiplies every font size on the
 * page, so a junk or hostile localStorage entry is the difference between a
 * readable board and an unusable one during the auction. Wrapped because
 * localStorage throws outright in some locked-down mobile browsers, and a
 * spectator's preference is never worth taking the board down for.
 */
export function readBoardScale() {
  try {
    const n = Number(window.localStorage.getItem(BOARD_SCALE_KEY));
    if (!Number.isFinite(n) || n <= 0) return MIN_SCALE;
    return Math.min(MAX_SCALE, Math.max(MIN_SCALE, n));
  } catch {
    return MIN_SCALE;
  }
}

export function writeBoardScale(scale) {
  try {
    window.localStorage.setItem(BOARD_SCALE_KEY, String(scale));
    return true;
  } catch {
    return false;                     // applies for this session, does not persist
  }
}

/**
 * Read once at mount, then component state. A failed WRITE still changes the
 * board for this session — the setting matters more than remembering it, and
 * the alternative is a control that appears to do nothing.
 */
export function useBoardScale() {
  const [scale, setScale] = useState(() => readBoardScale());
  const choose = useCallback((next) => {
    setScale(next);
    writeBoardScale(next);
  }, []);
  return [scale, choose];
}

/**
 * ══════════════════════════════════════════════════════════════════════
 * 🔴 THE STEPPER — the same three presets, one rung at a time.
 *
 * The preset row is the RIGHT control and it was in the wrong place: behind a
 * menu on /live, and one more row of permanent chrome on /watch. Text that is
 * too small is noticed by someone squinting at a screen, and "open a menu,
 * read three labels, pick one" is not what that person does — they give up and
 * lean in. A- / A+ is the gesture they already know.
 *
 * 🔴 IT IS NOT A SECOND SOURCE OF TRUTH. There is one scale per device and
 * both controls write it: the stepper moves along `SCALE_PRESETS`, the preset
 * row jumps to a member of it, and neither can produce a value the other
 * cannot show. The preset row STAYS — it is also the way out of TV mode, and
 * BoardScale's header says that may move but may not be removed.
 *
 * ⚠️ Steps from the NEAREST preset, not from an index. The stored value is a
 * number, it is clamped but not snapped on read, and a board carrying 1.2 from
 * some earlier build must still step somewhere sensible rather than to rung 0.
 * ══════════════════════════════════════════════════════════════════════
 */
export function stepScale(scale, dir) {
  const values = SCALE_PRESETS.map(p => p.scale);
  const cur = Number(scale);
  let at = 0;
  let best = Infinity;
  values.forEach((v, i) => {
    const d = Math.abs(v - (Number.isFinite(cur) ? cur : MIN_SCALE));
    if (d < best) { best = d; at = i; }
  });
  return values[Math.min(values.length - 1, Math.max(0, at + dir))];
}

/**
 * How long the stepper stays up on /watch after somebody touches the page.
 *
 * 🔴 A TV NOBODY TOUCHES STAYS CLEAN. That screen is across a room with no
 * pointer on it for two hours; a control parked in the corner of it is chrome
 * that never earns its place. The same page on a phone in a hand gets a mouse
 * move, a scroll or a tap within seconds of anybody wanting it.
 */
export const SCALE_IDLE_MS = 4000;

/**
 * True for SCALE_IDLE_MS after any mouse move, tap or key, false otherwise.
 *
 * Starts FALSE: the first render of a cast page is the one nobody has touched.
 * Passive listeners on window, torn down with the component — a page that runs
 * for a whole auction must not leave a timer or a listener behind.
 */
export function useIdleReveal(ms = SCALE_IDLE_MS) {
  const [visible, setVisible] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    const wake = () => {
      setVisible(true);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setVisible(false), ms);
    };
    const events = ["mousemove", "pointerdown", "touchstart", "keydown", "wheel"];
    for (const e of events) window.addEventListener(e, wake, { passive: true });
    return () => {
      for (const e of events) window.removeEventListener(e, wake);
      if (timer.current) clearTimeout(timer.current);
    };
  }, [ms]);

  return visible;
}

/**
 * A- / A+ , clamped at both ends of SCALE_PRESETS.
 *
 * `visible` is a DISPLAY state, not a mount condition: the pair keeps its box
 * either way, so revealing it on /watch cannot reflow the board under somebody
 * mid-auction. Hidden means it is also unclickable and out of the a11y tree —
 * a control faded to nothing that still takes a tap is worse than no control.
 */
export function BoardScaleStepper({ scale, onChange, visible = true, testId = "board-scale-stepper" }) {
  const atMin = Number(scale) <= MIN_SCALE;
  const atMax = Number(scale) >= MAX_SCALE;

  const btn = (enabled) => ({
    background: "transparent",
    border: `1px solid ${enabled ? theme.colors.border.default : theme.colors.border.subtle}`,
    borderRadius: theme.radius.sm,
    color: enabled ? theme.colors.text.muted : theme.colors.border.default,
    fontFamily: "inherit", fontSize: theme.fontSize.caption,
    fontWeight: theme.fontWeight.bold, lineHeight: 1,
    padding: "4px 8px", cursor: enabled ? "pointer" : "default",
  });

  return (
    <div
      data-testid={testId}
      // 🔴 THE HIDDEN STATE IS AN ATTRIBUTE, NOT JUST AN OPACITY. Opacity is a
      // computed style a test cannot read reliably through a transition, and
      // "is this control offered right now" is a fact about the page.
      data-visible={visible ? "1" : "0"}
      aria-hidden={visible ? undefined : "true"}
      style={{
        display: "flex", alignItems: "center", gap: 4,
        opacity: visible ? 1 : 0,
        pointerEvents: visible ? "auto" : "none",
        transition: "opacity 0.35s ease",
      }}
    >
      <button
        type="button"
        data-testid={`${testId}-down`}
        onClick={() => onChange(stepScale(scale, -1))}
        disabled={atMin}
        tabIndex={visible ? undefined : -1}
        aria-label="Smaller text"
        title="Smaller text"
        style={btn(!atMin)}
      >A-</button>
      <button
        type="button"
        data-testid={`${testId}-up`}
        onClick={() => onChange(stepScale(scale, 1))}
        disabled={atMax}
        tabIndex={visible ? undefined : -1}
        aria-label="Larger text"
        title="Larger text"
        style={btn(!atMax)}
      >A+</button>
    </div>
  );
}

export default function BoardScaleControl({ scale, onChange }) {
  return (
    <div data-testid="board-scale" style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <span style={{
        fontSize: theme.fontSize.micro, color: theme.colors.text.faint,
        textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: theme.fontWeight.bold,
      }}>Text size</span>
      {SCALE_PRESETS.map(p => {
        const on = scale === p.scale;
        return (
          <button
            key={p.key}
            onClick={() => onChange(p.scale)}
            aria-pressed={on}
            style={{
              background: on ? theme.colors.brand.orange : "transparent",
              border: `1px solid ${on ? theme.colors.brand.orange : theme.colors.border.default}`,
              borderRadius: theme.radius.sm,
              color: on ? "#fff" : theme.colors.text.muted,
              fontFamily: "inherit", fontSize: theme.fontSize.micro,
              padding: "2px 9px", cursor: "pointer",
            }}
          >{p.label}</button>
        );
      })}
    </div>
  );
}
